import { getRequest } from "./ApiService";
import Auth from "../routes/Auth";
import jstz from "jstz";
import moment from "moment-timezone";

// In-flight promise dedup, keyed by the fully-built URL. When two
// callers fire the same request in the same tick (typical example:
// a child component's onDecisionMade callback hitting the parent's
// fetchData at the same time the child does its own refresh), they
// share one promise and one network request. Cleared when the
// request resolves or rejects so subsequent calls re-hit the wire.
const _inflight = new Map();
const _shape = (body) => ({
  decisions: body.decisions,
  operations_decisions: body.operations_decisions,
  financial_decisions: body.financial_decisions,
  products: body.products,
  product_manufactures: body.product_manufactures,
  team_machines: body.team_machines,
  factories: body.factories,
  hours: body.hours,
  distributionCenter: body.distribution_centers,
  distributionCentersInfo: body.distribution_centers_info,
  distributionCenterPPE: body.ppes,
  teamDebts: body.debts,
  interest_payments: body.interest_payments,
  balanceSheet: body.balance_sheet,
  price_forecasts: body.price_forecasts,
  sales: body.sales,
  promos: body.promos,
  inventory_balances: body.inventory_balances,
  inventory_allocations: body.inventory_allocations,
  dc_lease_schedules: body.dc_lease_schedules,
  equities: body.equities,
  stock_repurchased: body.stock_repurchased,
  repurchased_shares: body.repurchased_shares,
  dividends_payments: body.dividends_payments,
  dividends_payment: body.dividends_payment,
  dividend_payments: body.dividend_payments,
  pre_moneys: body.pre_moneys,
  pre_money: body.pre_money,
  post_moneys: body.post_moneys,
  performance_moneys: body.performance_moneys,
  equity_cumulative: body.equity_cumulative,
  constants: body.constants,
  equity_clients: body.equity_clients,
  unit_sales: body.unit_sales,
  manufacture_costs: body.manufacture_costs,
  machine_depreciations: body.machine_depreciations,
  selling_price_factors: body.selling_price_factors,
  max_allowed_matrix: body.max_allowed_matrix,
  shipping_batch_size: body.shipping_batch_size,
  metrics_report: body.metrics_report,
  marketing_clients: body.marketing_clients,
  sales_clients: body.sales_clients,
});

/**
 * Fetches all team decision data from the consolidated endpoint.
 * Replaces individual calls to team/page/ceo, team/page/operations,
 * team/page/financials, team/products, team/debts, etc.
 */
export function fetchTeamDecisions(roundNumber, role, teamId, extraParams) {
  const base = `${process.env.REACT_APP_API_URL}app/team-decisions/`;
  const temporaryUser = Auth.getTemporaryUser();
  const params = [];
  if (roundNumber != null) params.push(`round_number=${roundNumber}`);
  if (role) params.push(`role=${role}`);
  if (teamId) params.push(`team_id=${teamId}`);
  else if (temporaryUser) params.push(`team_id=${temporaryUser.team_id}`);
  if (extraParams && typeof extraParams === 'object') {
    Object.keys(extraParams).forEach(k => {
      if (extraParams[k] != null) {
        params.push(`${k}=${extraParams[k]}`);
      }
    });
  }
  let url = params.length > 0 ? `${base}?${params.join('&')}` : base;

  if (_inflight.has(url)) {
    return _inflight.get(url);
  }

  const promise = getRequest(url)
    .then(({ body }) => {
      _inflight.delete(url);
      return _shape(body);
    })
    .catch(err => {
      _inflight.delete(url);
      throw err;
    });
  _inflight.set(url, promise);
  return promise;
}

/**
 * Fetches team status data (journal, submissions, unaccepted decisions)
 * from the consolidated endpoint.
 * Replaces individual calls to journal, unaccepted_decisions,
 * and the 4 submit_* journal queries.
 */
export function fetchTeamStatus(roundNumber) {
  const base = `${process.env.REACT_APP_API_URL}app/team-status/`;
  const temporaryUser = Auth.getTemporaryUser();
  let url = roundNumber != null ? `${base}?round_number=${roundNumber}` : base;
  if (temporaryUser) {
    url += `${roundNumber != null ? '&' : '?'}team_id=${temporaryUser.team_id}`;
  }

  return getRequest(url).then(({ body }) => {
    const timezone = jstz.determine();

    const submission_data = {
      pd: { unaccepted: body.unaccepted_decisions.PD || 0, date: 'N/A' },
      pm: { unaccepted: body.unaccepted_decisions.PM || 0, date: 'N/A' },
      op: { unaccepted: body.unaccepted_decisions.OP || 0, date: 'N/A' },
      fn: { unaccepted: body.unaccepted_decisions.FI || 0, date: 'N/A' },
    };

    const submissionMap = {
      '/api/teams/submit_development/': 'pd',
      '/api/teams/submit_marketing/': 'pm',
      '/api/teams/submit_operations/': 'op',
      '/api/teams/submit_finance/': 'fn',
    };

    Object.keys(body.last_submissions).forEach(path => {
      const entry = body.last_submissions[path];
      const type = submissionMap[path];
      if (type && entry && entry.requested_at) {
        submission_data[type].date = moment(entry.requested_at)
          .tz(timezone.name())
          .format('MM/DD hh:mmA');
      }
    });

    return {
      journal: body.journal,
      unaccepted_decisions: body.unaccepted_decisions,
      last_submissions: body.last_submissions,
      submission_data,
    };
  });
}



// WEBPACK FOOTER //
// ./src/api/TeamDecisionsService.js